import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Users, Calendar, MapPin, Shield, Star, DollarSign,
  ChevronLeft, X, Loader, AlertCircle, Check, Gift,
  Clock, Swords
} from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { adminAPI, authAPI } from '../api';

/* ── Helpers ──────────────────────────────────────────────────────────── */
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

function statusLabel(s) {
  return { registration: 'Inscriptions ouvertes', seeding: 'Seeding', 'in-progress': 'En cours', finished: 'Terminé' }[s] || s;
}
function statusClass(s) {
  return { registration: 'lp-tc-status--registration', seeding: 'lp-tc-status--seeding', 'in-progress': 'lp-tc-status--in-progress', finished: 'lp-tc-status--finished' }[s] || '';
}
function formatLabel(f) {
  return { 'single-elimination': 'Élim. simple', 'double-elimination': 'Élim. double' }[f] || f;
}
const TROPHY_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

const API = process.env.REACT_APP_API_URL || '/api';

/* ── Bracket ──────────────────────────────────────────────────────────── */

// Ordre d'affichage des rounds internes
const ROUND_ORDER = [
  'round-1','round-2','round-3','round-4',
  'round-of-16','quarterfinals','semifinals','final','grand-final',
];
const ROUND_LABELS = {
  'round-1':      'Round 1',
  'round-2':      'Round 2',
  'round-3':      'Round 3',
  'round-4':      'Round 4',
  'round-of-16':  'Huitièmes de finale',
  'quarterfinals':'Quarts de finale',
  'semifinals':   'Demi-finales',
  'final':        'Finale',
  'grand-final':  'Grande Finale',
};

// ── Carte d'un match ──────────────────────────────────────────────────────
function MatchCard({ match }) {
  const teamA = match.teamA;
  const teamB = match.teamB;

  // Gagnant
  const aWon = match.winner && teamA && match.winner.id === teamA.id;
  const bWon = match.winner && teamB && match.winner.id === teamB.id;

  // Scores
  const scoreA = match.scoreA;
  const scoreB = match.scoreB;

  // État
  const isDone = match.status === 'finished';
  const isLive = match.status === 'in-progress';
  const isBye  = false;

  return (
    <div className={[
      'tp-match-card',
      isDone ? 'tp-match--done' : '',
      isLive ? 'tp-match--live' : '',
    ].join(' ')}>

      {isLive && <span className="tp-match-live-dot" />}

      {/* Équipe A */}
      <div className={[
        'tp-match-team',
        aWon ? 'tp-match-team--winner' : '',
        !teamA ? 'tp-match-team--tbd' : '',
        !aWon && !bWon ? '' : (!aWon ? 'tp-match-team--loser' : ''),
      ].join(' ')}>
        <span className="tp-match-team-name">{teamA?.name || 'TBD'}</span>
        {isDone && scoreA != null && <span className="tp-match-score">{scoreA}</span>}
        {aWon && <Check size={11} className="tp-match-winner-icon" />}
      </div>

      <div className="tp-match-vs">vs</div>

      {/* Équipe B */}
      <div className={[
        'tp-match-team',
        bWon ? 'tp-match-team--winner' : '',
        !teamB ? 'tp-match-team--tbd' : '',
        !aWon && !bWon ? '' : (!bWon ? 'tp-match-team--loser' : ''),
      ].join(' ')}>
        <span className="tp-match-team-name">
          {isBye ? 'BYE' : (teamB?.name || 'TBD')}
        </span>
        {isDone && scoreB != null && <span className="tp-match-score">{scoreB}</span>}
        {bWon && <Check size={11} className="tp-match-winner-icon" />}
      </div>
    </div>
  );
}

// ── Section Bracket complète ──────────────────────────────────────────────
function BracketSection({ tournament }) {
  const [bracket,   setBracket]   = useState(null);
  const [bLoading,  setBLoading]  = useState(true);
  const [bError,    setBError]    = useState('');

  useEffect(() => {
    setBLoading(true);
    setBError('');
    setBracket(null);

    fetch(`${API}/tournaments/${tournament.id}/bracket`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setBracket(data);
      })
      .catch(e => setBError(e.message))
      .finally(() => setBLoading(false));
  }, [tournament.id]);

  // Rounds triés
  const sortedRounds = useMemo(() => {
    if (!bracket) return [];
    const keys = Object.keys(bracket);
    return [...keys].sort((a, b) => {
      const ia = ROUND_ORDER.indexOf(a);
      const ib = ROUND_ORDER.indexOf(b);
      if (ia < 0 && ib < 0) return 0;
      if (ia < 0) return 1;
      if (ib < 0) return -1;
      return ia - ib;
    });
  }, [bracket]);

  const hasMatches = sortedRounds.length > 0;

  return (
    <div className="tp-bracket-section">

      {/* En-tête */}
      <div className="tp-bracket-header">
        <div className="tp-bracket-title">
          <Swords size={20} />
          <h2>Bracket</h2>
        </div>
      </div>

      {/* Corps */}
      {bLoading ? (
        <div className="tp-bracket-state">
          <Loader size={26} className="tp-spin" />
          <span>Chargement du bracket…</span>
        </div>
      ) : bError ? (
        <div className="tp-bracket-state tp-bracket-state--error">
          <AlertCircle size={22} />
          <span>{bError}</span>
        </div>
      ) : !hasMatches ? (
        <div className="tp-bracket-state">
          <Swords size={38} />
          <p>Le bracket n'a pas encore été généré.</p>
          {tournament.status === 'registration' && (
            <p className="tp-bracket-hint">Disponible dès la fermeture des inscriptions.</p>
          )}
        </div>
      ) : (

        /* ── Vue Bracket ── */
        <div className="tp-bracket-scroll">
          <div className="tp-bracket-rounds">
            {sortedRounds.map(roundKey => (
              <div key={roundKey} className="tp-bracket-round">
                <div className="tp-bracket-round-label">
                  {ROUND_LABELS[roundKey] || roundKey}
                </div>
                <div className="tp-bracket-matches">
                  {(bracket[roundKey] || []).map(match => (
                    <MatchCard
                      key={match.id}
                      match={match}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Modale d'inscription ─────────────────────────────────────────────── */
function JoinModal({ tournament, onClose, onJoined }) {
  const [myTeams, setMyTeams]   = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading]   = useState(true);
  const [joining, setJoining]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    authAPI.getMyTeams().then(res => {
      setMyTeams(res.data || []);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, []);

  // Bloquer scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleJoin = async () => {
    if (!selected) return;
    setJoining(true);
    setError('');
    try {
      await adminAPI.joinTournament(tournament._id, selected);
      onJoined();
    } catch (e) {
      setError(e.message || 'Erreur lors de l\'inscription');
    } finally {
      setJoining(false);
    }
  };

  // Équipes déjà inscrites (IDs)
  const registeredIds = new Set((tournament.teams || []).map(t => t._id || t));

  return (
    <motion.div
      className="tp-modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        className="tp-modal"
        initial={{ opacity: 0, y: 32, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        role="dialog" aria-modal="true"
      >
        <div className="tp-modal-header">
          <h3><Users size={18} /> Inscrire mon équipe</h3>
          <button className="tp-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="tp-modal-body">
          <p className="tp-modal-tournament-name">
            <Trophy size={15} /> {tournament.name}
          </p>

          {loading ? (
            <div className="tp-modal-loading"><Loader size={20} className="tp-spin" /> Chargement…</div>
          ) : myTeams.length === 0 ? (
            <div className="tp-modal-empty">
              <Users size={36} />
              <p>Vous n'avez pas d'équipe.</p>
              <Link to="/teams" className="tp-modal-create-link">Créer une équipe →</Link>
            </div>
          ) : (
            <div className="tp-modal-teams">
              {myTeams.map(team => {
                const alreadyIn = registeredIds.has(team._id);
                return (
                  <label
                    key={team._id}
                    className={`tp-modal-team-row${selected === team._id ? ' selected' : ''}${alreadyIn ? ' disabled' : ''}`}
                  >
                    <input
                      type="radio"
                      name="team"
                      value={team._id}
                      disabled={alreadyIn}
                      checked={selected === team._id}
                      onChange={() => setSelected(team._id)}
                    />
                    <div className="tp-modal-team-info">
                      <span className="tp-modal-team-name">{team.name}</span>
                      {team.tag && <span className="tp-modal-team-tag">#{team.tag}</span>}
                      <span className="tp-modal-team-members">{team.members?.length || 0} membres</span>
                    </div>
                    {alreadyIn && <span className="tp-modal-already-in"><Check size={13} /> Inscrite</span>}
                  </label>
                );
              })}
            </div>
          )}

          <AnimatePresence>
            {error && (
              <motion.div className="tp-modal-error"
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <AlertCircle size={14} /> {error}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="tp-modal-footer">
          <button className="tp-btn tp-btn--ghost" onClick={onClose} disabled={joining}>Annuler</button>
          <button
            className="tp-btn tp-btn--primary"
            onClick={handleJoin}
            disabled={!selected || joining}
          >
            {joining ? <><Loader size={15} className="tp-spin" /> Inscription…</> : <><Check size={15} /> Confirmer</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Page principale ──────────────────────────────────────────────────── */
export default function TournamentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [showJoin, setShowJoin]     = useState(false);
  const [joinSuccess, setJoinSuccess] = useState(false);

  const loadTournament = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.getTournament(id);
      setTournament(res.data);
    } catch (e) {
      setError('Tournoi introuvable');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTournament(); }, [loadTournament]);

  const handleJoined = () => {
    setShowJoin(false);
    setJoinSuccess(true);
    loadTournament();
  };

  if (loading) return (
    <div className="tp-loading-page">
      <Loader size={32} className="tp-spin" />
      <p>Chargement du tournoi…</p>
    </div>
  );

  if (error || !tournament) return (
    <div className="tp-error-page">
      <Trophy size={48} />
      <p>{error || 'Tournoi introuvable'}</p>
      <button className="tp-btn tp-btn--ghost" onClick={() => navigate('/')}>Retour à l'accueil</button>
    </div>
  );

  const teamsCount    = tournament.teams?.length || 0;
  const spotsLeft     = tournament.maxTeams - teamsCount;
  const canRegister   = tournament.status === 'registration' && spotsLeft > 0;
  const hasRewards    = tournament.rewards?.length > 0;
  const hasSponsors   = tournament.sponsors?.length > 0;
  const hasFee        = tournament.entryFee?.amount > 0;
  const sortedRewards = hasRewards
    ? [...tournament.rewards].sort((a, b) => a.place - b.place)
    : [];

  return (
    <motion.div
      className="tp-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* ── Retour ── */}
      <div className="tp-back">
        <button className="tp-back-btn" onClick={() => navigate(-1)}>
          <ChevronLeft size={18} /> Retour
        </button>
      </div>

      {/* ── Hero ── */}
      <div className={`tp-hero${tournament.logo ? ' tp-hero--has-banner' : ''}`}>
        {tournament.logo ? (
          <div className="tp-hero-banner" style={{ backgroundImage: `url(${tournament.logo})` }} />
        ) : (
          <div className="tp-hero-bg" />
        )}
        <div className="tp-hero-overlay" />
        <div className="tp-hero-content">
          <div className="tp-hero-info">
            <div className="tp-badges">
              <span className={`tp-badge tp-status ${statusClass(tournament.status)}`}>
                {statusLabel(tournament.status)}
              </span>
              <span className="tp-badge tp-region"><MapPin size={12} /> {tournament.region}</span>
              <span className="tp-badge tp-format"><Swords size={12} /> {formatLabel(tournament.format)}</span>
            </div>
            <h1 className="tp-title">{tournament.name}</h1>
            {tournament.description && (
              <p className="tp-description">{tournament.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Bande sponsors (pleine largeur, sous le hero) ── */}
      {hasSponsors && (
        <div className="tp-sponsors-banner">
          <span className="tp-sponsors-banner-label"><Star size={13} /> Sponsors</span>
          <div className="tp-sponsors-banner-list">
            {tournament.sponsors.map((s, i) => (
              <a
                key={i}
                href={s.website || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="tp-sponsors-banner-item"
                title={s.name}
              >
                {s.logoUrl && (
                  <img
                    src={s.logoUrl}
                    alt={s.name}
                    className="tp-sponsors-banner-logo"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
                <span className="tp-sponsors-banner-name">{s.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ── Contenu principal ── */}
      <div className="tp-container">

        {/* Succès inscription */}
        <AnimatePresence>
          {joinSuccess && (
            <motion.div className="tp-success-banner"
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <Check size={18} /> Votre équipe a bien été inscrite au tournoi !
              <button onClick={() => setJoinSuccess(false)}><X size={14} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="tp-grid">
          {/* ── Colonne gauche (infos + sponsors + récompenses + équipes) ── */}
          <div className="tp-main">

            {/* Infos clés */}
            <div className="tp-card">
              <h2 className="tp-card-title"><Calendar size={18} /> Informations</h2>
              <div className="tp-info-grid">
                <div className="tp-info-item">
                  <span className="tp-info-label"><Calendar size={14} /> Date de début</span>
                  <span className="tp-info-value">{formatDate(tournament.startDate)}</span>
                </div>
                {tournament.registrationDeadline && (
                  <div className="tp-info-item">
                    <span className="tp-info-label"><Clock size={14} /> Clôture des inscriptions</span>
                    <span className="tp-info-value">{formatDate(tournament.registrationDeadline)}</span>
                  </div>
                )}
                <div className="tp-info-item">
                  <span className="tp-info-label"><Users size={14} /> Équipes</span>
                  <span className="tp-info-value">
                    <span className="tp-teams-count">{teamsCount}</span>
                    <span className="tp-teams-sep">/</span>
                    <span className="tp-teams-max">{tournament.maxTeams}</span>
                    {spotsLeft > 0 && canRegister && (
                      <span className="tp-spots-left">{spotsLeft} place{spotsLeft > 1 ? 's' : ''} restante{spotsLeft > 1 ? 's' : ''}</span>
                    )}
                  </span>
                </div>
                <div className="tp-info-item">
                  <span className="tp-info-label"><DollarSign size={14} /> Frais d'inscription</span>
                  <span className="tp-info-value">
                    {hasFee
                      ? `${tournament.entryFee.amount} ${tournament.entryFee.currency}`
                      : <span className="tp-free-badge">Gratuit</span>}
                  </span>
                </div>
                {tournament.prizePool && (
                  <div className="tp-info-item">
                    <span className="tp-info-label"><Gift size={14} /> Prize pool</span>
                    <span className="tp-info-value">{tournament.prizePool}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Règles */}
            {tournament.rules && (
              <div className="tp-card">
                <h2 className="tp-card-title"><Shield size={18} /> Règlement</h2>
                <p className="tp-rules-text">{tournament.rules}</p>
              </div>
            )}

            {/* Récompenses */}
            {hasRewards && (
              <div className="tp-card">
                <h2 className="tp-card-title"><Gift size={18} /> Récompenses</h2>
                <div className="tp-rewards-list">
                  {sortedRewards.map((r, i) => (
                    <div key={i} className="tp-reward-item">
                      <div className="tp-reward-place" style={{ color: TROPHY_COLORS[i] || '#aaa' }}>
                        <Trophy size={16} />
                        <span>{r.place === 1 ? '1er' : r.place === 2 ? '2ème' : r.place === 3 ? '3ème' : `${r.place}ème`}</span>
                      </div>
                      <div className="tp-reward-info">
                        {r.description && <span className="tp-reward-desc">{r.description}</span>}
                        {r.amount && <span className="tp-reward-amount">{r.amount}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Équipes inscrites */}
            {teamsCount > 0 && (
              <div className="tp-card">
                <h2 className="tp-card-title"><Users size={18} /> Équipes inscrites ({teamsCount}/{tournament.maxTeams})</h2>
                <div className="tp-teams-grid">
                  {tournament.teams.map((team, i) => (
                    <div key={team._id || i} className="tp-team-chip">
                      <Shield size={13} />
                      <span>{team.name || 'Équipe'}</span>
                      {team.tag && <span className="tp-team-chip-tag">#{team.tag}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar (sponsors + CTA) ── */}
          <div className="tp-sidebar">

            {/* CTA inscription */}
            <div className="tp-cta-card">
              {canRegister ? (
                <>
                  <div className="tp-cta-info">
                    <span className="tp-cta-spots">{spotsLeft} place{spotsLeft > 1 ? 's' : ''} disponible{spotsLeft > 1 ? 's' : ''}</span>
                    {hasFee && <span className="tp-cta-fee">{tournament.entryFee.amount} {tournament.entryFee.currency}</span>}
                  </div>
                  {isAuthenticated ? (
                    <button className="tp-btn tp-btn--primary tp-btn--large" onClick={() => setShowJoin(true)}>
                      <Users size={18} /> Rejoindre avec mon équipe
                    </button>
                  ) : (
                    <Link to="/riot-login" className="tp-btn tp-btn--primary tp-btn--large">
                      <Shield size={18} /> Se connecter pour rejoindre
                    </Link>
                  )}
                  {hasFee && (
                    <p className="tp-cta-fee-note">
                      💳 Le paiement sera disponible prochainement.
                    </p>
                  )}
                </>
              ) : tournament.status === 'finished' ? (
                <div className="tp-cta-closed">
                  <Trophy size={32} />
                  <p>Tournoi terminé</p>
                </div>
              ) : (
                <div className="tp-cta-closed">
                  <Clock size={32} />
                  <p>{tournament.status === 'registration' ? 'Tournoi complet' : 'Inscriptions fermées'}</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── Section Bracket ── */}
      <BracketSection tournament={tournament} />

      {/* ── Modale inscription ── */}
      <AnimatePresence>
        {showJoin && (
          <JoinModal
            key="join-modal"
            tournament={tournament}
            onClose={() => setShowJoin(false)}
            onJoined={handleJoined}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
